import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { registrationsApi, activitiesApi } from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { getCategoryInfo, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { Calendar, MapPin, Heart, Search, ChevronRight, Clock } from 'lucide-react';

const isDatePast = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return d < now;
};

const isActivityFullyPast = (activity) => {
  if (!activity?.dates || activity.dates.length === 0) return false;
  return activity.dates.every(d => isDatePast(d.date));
};

const MyActivities = () => {
  const { t, language } = useLanguage();
  const [registrations, setRegistrations] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [regRes, favRes] = await Promise.all([
        registrationsApi.getMine(),
        activitiesApi.getFavorites()
      ]);
      setRegistrations(regRes.data);
      setFavorites(favRes.data);
    } catch (error) {
      toast.error(t('msg.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const confirmedRegistrations = registrations.filter(r => !r.is_waitlist && r.activity);
  const waitlistRegistrations = registrations.filter(r => r.is_waitlist && r.activity);

  const { activeRegistrations, pastRegistrations } = useMemo(() => {
    const active = [];
    const past = [];
    confirmedRegistrations.forEach(r => {
      const selDate = r.selected_date || r.activity?.dates?.[0];
      if (isDatePast(selDate?.date)) {
        past.push(r);
      } else {
        active.push(r);
      }
    });
    return { activeRegistrations: active, pastRegistrations: past };
  }, [confirmedRegistrations]);

  const { activeFavorites, pastFavorites } = useMemo(() => {
    const active = [];
    const past = [];
    favorites.forEach(a => {
      if (isActivityFullyPast(a)) {
        past.push(a);
      } else {
        active.push(a);
      }
    });
    return { activeFavorites: active, pastFavorites: past };
  }, [favorites]);

  const handleRemoveFavorite = async (activityId) => {
    try {
      await activitiesApi.toggleFavorite(activityId);
      setFavorites(prev => prev.filter(a => a.activity_id !== activityId));
    } catch (error) {
      toast.error(t('msg.error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32 md:pb-8 bg-white">
      <div className="max-w-3xl mx-auto">
        <div className="sticky top-14 md:top-11 z-30 bg-white pt-4 px-4 md:px-6 pb-4">
          <h1 className="font-rubik text-2xl font-bold text-gray-500">
            {t('nav.myActivities')}
          </h1>
        </div>

        <div className="px-4 md:px-6">

        <Tabs defaultValue="confirmed" className="w-full">
          <TabsList className="w-full mb-6 bg-gray-100 p-1 rounded-xl">
            <TabsTrigger 
              value="confirmed" 
              className="flex-1 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
              data-testid="tab-confirmed"
            >
              {t('myActivities.reservations')} ({confirmedRegistrations.length})
            </TabsTrigger>
            <TabsTrigger 
              value="waitlist"
              className="flex-1 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
              data-testid="tab-waitlist"
            >
              {t('myActivities.waitlist')} ({waitlistRegistrations.length})
            </TabsTrigger>
            <TabsTrigger 
              value="favorites"
              className="flex-1 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
              data-testid="tab-favorites"
            >
              <Heart className="w-3.5 h-3.5 mr-1 inline" />
              {t('myActivities.favorites')} ({favorites.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="confirmed">
            {confirmedRegistrations.length === 0 ? (
              <EmptyState 
                title={t('myActivities.noReservations')}
                description={t('myActivities.explore')}
                buttonText={t('nav.explore')}
                buttonLink="/"
              />
            ) : (
              <div className="space-y-4">
                {activeRegistrations.map((reg) => (
                  <RegistrationCard 
                    key={reg.registration_id} 
                    registration={reg} 
                    language={language}
                    t={t}
                  />
                ))}
                {pastRegistrations.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 pt-4 pb-1" data-testid="past-registrations-header">
                      <Clock className="w-4 h-4 text-gray-300" />
                      <span className="text-sm font-medium text-gray-400">{t('myActivities.pastActivities')}</span>
                      <div className="flex-1 h-px bg-gray-100" />
                    </div>
                    {pastRegistrations.map((reg) => (
                      <RegistrationCard 
                        key={reg.registration_id} 
                        registration={reg} 
                        language={language}
                        t={t}
                        isPast
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="waitlist">
            {waitlistRegistrations.length === 0 ? (
              <EmptyState 
                title={t('myActivities.noWaitlist')}
              />
            ) : (
              <div className="space-y-4">
                {waitlistRegistrations.map((reg) => (
                  <RegistrationCard 
                    key={reg.registration_id} 
                    registration={reg}
                    isWaitlist 
                    language={language}
                    t={t}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="favorites">
            {favorites.length === 0 ? (
              <EmptyState 
                title={t('myActivities.noFavorites')}
                buttonText={t('nav.explore')}
                buttonLink="/"
              />
            ) : (
              <div className="space-y-4">
                {activeFavorites.map((activity) => (
                  <FavoriteCard 
                    key={activity.activity_id}
                    activity={activity}
                    language={language}
                    t={t}
                    onRemove={handleRemoveFavorite}
                  />
                ))}
                {pastFavorites.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 pt-4 pb-1" data-testid="past-favorites-header">
                      <Clock className="w-4 h-4 text-gray-300" />
                      <span className="text-sm font-medium text-gray-400">{t('myActivities.pastActivities')}</span>
                      <div className="flex-1 h-px bg-gray-100" />
                    </div>
                    {pastFavorites.map((activity) => (
                      <FavoriteCard 
                        key={activity.activity_id}
                        activity={activity}
                        language={language}
                        t={t}
                        onRemove={handleRemoveFavorite}
                        isPast
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ title, description, buttonText, buttonLink }) => (
  <div className="text-center py-16">
    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
      <Calendar className="w-8 h-8 text-gray-300" />
    </div>
    <p className="text-gray-500 font-medium mb-2">{title}</p>
    <p className="text-gray-400 text-sm mb-6">{description}</p>
    {buttonText && buttonLink && (
      <Link to={buttonLink}>
        <Button className="bg-primary hover:bg-primary-hover rounded-xl">
          <Search className="w-4 h-4 mr-2" />
          {buttonText}
        </Button>
      </Link>
    )}
  </div>
);

const RegistrationCard = ({ registration, isWaitlist, language, t, isPast }) => {
  const activity = registration.activity;
  if (!activity) return null;

  const selectedDate = registration.selected_date || activity.dates[0];
  const isPaid = activity.price > 0;
  const paymentConfirmed = registration.status === 'confirmed' && isPaid;
  const paymentPending = registration.status === 'pending_payment';
  const paymentOverdue = registration.status === 'payment_overdue';
  const proofSent = registration.payment_proof_sent;
  const isCancelled = registration.status === 'cancelled';
  const paymentNotFound = registration.payment_status === 'not_found' || registration.status === 'payment_not_found';
  const qty = registration.quantity || 1;

  return (
    <Link 
      to={`/activity/${activity.activity_id}`}
      className={`block bg-white rounded-xl border border-gray-100 overflow-hidden transition-shadow ${isPast ? 'opacity-60' : 'hover:shadow-airbnb'}`}
      data-testid={`registration-card-${registration.registration_id}`}
    >
      <div className="flex">
        <div className="w-28 md:w-36 flex-shrink-0 relative">
          <img 
            src={activity.cover_image || 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=400&q=80'} 
            alt={activity.title}
            className={`w-full h-full object-cover ${isPast ? 'grayscale' : ''}`}
          />
        </div>
        <div className="flex-1 p-3 flex flex-col justify-between min-h-[7rem]">
          <div>
            {isWaitlist && (
              <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium mb-1">
                {t('myActivities.waitlist')}
              </span>
            )}
            {!isWaitlist && paymentConfirmed && (
              <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium mb-1" data-testid="badge-payment-confirmed">
                {t('myActivities.paymentConfirmed')}
              </span>
            )}
            {!isWaitlist && (paymentPending || paymentOverdue) && proofSent && (
              <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-600 rounded text-xs font-medium mb-1" data-testid="badge-proof-sent">
                Confirmación pendiente
              </span>
            )}
            {!isWaitlist && paymentPending && !paymentNotFound && !proofSent && (
              <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium mb-1" data-testid="badge-payment-pending">
                {t('myActivities.paymentPending')} · 24h para pagar
              </span>
            )}
            {!isWaitlist && paymentOverdue && !proofSent && (
              <span className="inline-block px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium mb-1" data-testid="badge-payment-overdue">
                Plazo vencido · 24h de gracia restantes
              </span>
            )}
            {isCancelled && (
              <span className="inline-block px-2 py-0.5 bg-gray-200 text-gray-500 rounded text-xs font-medium mb-1" data-testid="badge-cancelled">
                {t('myActivities.cancelled')}
              </span>
            )}
            {!isWaitlist && paymentNotFound && (
              <span className="inline-block px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium mb-1" data-testid="badge-payment-not-found">
                Pago no encontrado
              </span>
            )}
            <h3 className="font-rubik font-semibold text-sm text-gray-500 mb-0.5 line-clamp-1">
              {activity.title}
            </h3>
            <p className="text-xs text-gray-400">
              {formatDate(selectedDate?.date, language === 'es' ? 'es-MX' : 'en-US')}
              {qty > 1 && <span className="ml-2 text-primary font-medium">x{qty} lugares</span>}
            </p>
          </div>
          <div className="flex items-center text-xs text-gray-400 mt-1">
            <MapPin className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
            <span className="truncate">{activity.city}</span>
          </div>
        </div>
        <div className="flex items-center pr-3">
          <ChevronRight className="w-5 h-5 text-gray-300" />
        </div>
      </div>
    </Link>
  );
};

const FavoriteCard = ({ activity, language, t, onRemove, isPast }) => {
  const selectedDate = activity.dates?.[0];

  return (
    <Link 
      to={`/activity/${activity.activity_id}`}
      className={`block bg-white rounded-xl border border-gray-100 overflow-hidden transition-shadow ${isPast ? 'opacity-60' : 'hover:shadow-airbnb'}`}
      data-testid={`favorite-card-${activity.activity_id}`}
    >
      <div className="flex">
        <div className="w-28 md:w-36 flex-shrink-0">
          <img 
            src={activity.cover_image || 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=400&q=80'} 
            alt={activity.title}
            className={`w-full h-full object-cover ${isPast ? 'grayscale' : ''}`}
          />
        </div>
        <div className="flex-1 p-3 flex flex-col justify-between min-h-[7rem]">
          <div>
            {isPast && (
              <span className="inline-block px-2 py-0.5 bg-gray-200 text-gray-500 rounded text-xs font-medium mb-1" data-testid="badge-favorite-past">
                {t('myActivities.pastActivity')}
              </span>
            )}
            <h3 className="font-rubik font-semibold text-sm text-gray-500 mb-0.5 line-clamp-1">
              {activity.title}
            </h3>
            <p className="text-xs text-gray-400 mb-0.5">
              {t(`category.${activity.category}`)} · {activity.city}
            </p>
            {selectedDate && (
              <p className="text-xs text-gray-400">
                {formatDate(selectedDate?.date, language === 'es' ? 'es-MX' : 'en-US')}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center pr-3 gap-2">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove(activity.activity_id);
            }}
            className="p-2 rounded-full hover:bg-red-50 transition-colors"
            data-testid={`remove-favorite-${activity.activity_id}`}
          >
            <Heart className="w-5 h-5 fill-red-500 text-red-500" />
          </button>
        </div>
      </div>
    </Link>
  );
};

export default MyActivities;
